// ==================== AUTH PROVIDER COMPONENT ====================

import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { ROUTES, APP_CONSTANTS } from '../../constants';
import { websocketService } from '../../services/websocket.service';
import type { User, AuthTokens, LoginCredentials, RegisterData } from '../../types';
import { hasValidSubscriptionAccess } from '../../utils/subscription';
import { AuthContext } from './AuthContext';

// ==================== ERROR MESSAGES ====================

const ERROR_MESSAGES = {
  INVALID_CREDENTIALS: 'Invalid email or password.',
  INVALID_CREDENTIALS_RETRY: 'Invalid email or password. Please try again.',
  ACCOUNT_DISABLED: 'Your account has been disabled. Please contact support.',
  ACCOUNT_NOT_FOUND: 'No account found with this email address.',
  ACCOUNT_NOT_VERIFIED: 'Please verify your email address to continue.',
  NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection.',
  GENERIC: 'Something went wrong. Please try again later.',
  EMAIL_EXISTS: 'An account with this email already exists.',
  INVALID_DATA: 'Invalid registration data. Please check your inputs.',
};

// Error codes from backend
const ERROR_CODES = {
  EMAIL_NOT_VERIFIED: 'ERR_1105',
};

// ==================== HELPER FUNCTIONS ====================


// ==================== PROVIDER PROPS ====================

interface AuthProviderProps {
  children: ReactNode;
}

// ==================== AUTH PROVIDER ====================

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect based on user role and status
  const redirectAfterLogin = useCallback((loggedInUser: User) => {
    const from = (location.state as { from?: Location })?.from?.pathname;

    if (loggedInUser.role === 'SUPER_ADMIN') {
      // Super Admin goes to admin dashboard
      navigate(from || ROUTES.ADMIN.DASHBOARD, { replace: true });
    } else if (loggedInUser.role === 'RESELLER') {
      navigate(ROUTES.RESELLER.DASHBOARD, { replace: true });
    } else if (loggedInUser.role === 'SUB_CUSTOMER') {
      // Sub-account: access follows the main account's plan (no plan pages for them)
      if (hasValidSubscriptionAccess(loggedInUser.organization?.subscription)) {
        navigate(from || ROUTES.CUSTOMER.DASHBOARD, { replace: true });
      } else {
        navigate(ROUTES.CUSTOMER.ACCOUNT_UNAVAILABLE, { replace: true });
      }
    } else {
      // Customer - check status
      if (!loggedInUser.isActive) {
        // Account pending approval
        navigate(ROUTES.CUSTOMER.ROOT + '/pending', { replace: true });
      } else if (!hasValidSubscriptionAccess(loggedInUser.organization?.subscription)) {
        // No valid subscription access - show plans
        // Note: CANCELLED subscriptions with remaining time WILL have access
        navigate(ROUTES.CUSTOMER.ROOT + '/select-plan', { replace: true });
      } else {
        // Active customer with valid subscription access
        navigate(from || ROUTES.CUSTOMER.DASHBOARD, { replace: true });
      }
    }
  }, [navigate, location.state]);

  // Check for existing auth on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = authService.getStoredUser();
      const storedTokens = authService.getStoredTokens();

      if (storedUser && storedTokens) {
        // Set initial state from localStorage immediately
        setUser(storedUser);
        setTokens(storedTokens);
        
        // Mark as initialized first so the app can render
        setIsLoading(false);
        setIsInitialized(true);

        // Then verify token with backend in background
        // The axios interceptor will handle token refresh if needed
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            // Update with fresh user data - API returns user directly in data
            setUser(response.data);
            authService.updateStoredUser(response.data);
          } else if (response.statusCode === 401 && !authService.getStoredTokens()) {
            // Only clear if tokens were already cleared by interceptor
            setUser(null);
            setTokens(null);
          }
        } catch {
          // Silent fail - interceptor handles logout if needed
        }
      } else {
        // No stored auth - just mark as initialized
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initAuth();
  }, []);

  // Listen for storage changes (e.g., when tokens are cleared by interceptor or another tab)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // If access token was removed, clear auth state
      if (event.key === APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN && event.newValue === null) {
        setUser(null);
        setTokens(null);
        navigate(ROUTES.AUTH.LOGIN, { replace: true });
      }
      // If user data changed, sync it
      if (event.key === APP_CONSTANTS.STORAGE_KEYS.USER && event.newValue) {
        try {
          const newUser = JSON.parse(event.newValue);
          setUser(newUser);
        } catch {
          // Invalid JSON, ignore
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // Update tokens (can be called when tokens are refreshed by interceptor)
  const updateTokens = useCallback((newTokens: AuthTokens) => {
    setTokens(newTokens);
  }, []);

  // Update user (for profile updates)
  const updateUser = useCallback((updatedUser: User) => {
    setUser(updatedUser);
    authService.updateStoredUser(updatedUser);
  }, []);

  // Login handler
  const login = useCallback(async (credentials: LoginCredentials): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    const response = await authService.login(credentials);

    if (response.success && response.data) {
      const { user: loggedInUser, tokens: authTokens } = response.data;
      setUser(loggedInUser);
      setTokens(authTokens);
      setIsLoading(false);

      // Redirect based on role and status
      redirectAfterLogin(loggedInUser);
      return true;
    } else {
      // Check if email is not verified - redirect to verification page
      const errorCode = response.errorCode;
      const isEmailNotVerified = 
        errorCode === ERROR_CODES.EMAIL_NOT_VERIFIED ||
        response.message?.toLowerCase().includes('verify') ||
        response.message?.toLowerCase().includes('not verified');

      if (isEmailNotVerified) {
        setIsLoading(false);
        // Redirect to verify email page with email in state
        navigate(ROUTES.AUTH.VERIFY_EMAIL, { 
          state: { email: credentials.email },
          replace: true 
        });
        return false;
      }

      // Handle other errors - extract meaningful message
      let errorMessage: string = ERROR_MESSAGES.INVALID_CREDENTIALS;
      
      if (response.message) {
        errorMessage = response.message;
      } else if (response.statusCode === 401) {
        errorMessage = ERROR_MESSAGES.INVALID_CREDENTIALS_RETRY;
      } else if (response.statusCode === 403) {
        errorMessage = ERROR_MESSAGES.ACCOUNT_DISABLED;
      } else if (response.statusCode === 404) {
        errorMessage = ERROR_MESSAGES.ACCOUNT_NOT_FOUND;
      } else if (response.statusCode === 0 || !response.statusCode) {
        errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      return false;
    }
  }, [redirectAfterLogin, navigate]);

  // Register handler
  const register = useCallback(async (data: RegisterData): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    setError(null);

    const response = await authService.register(data);

    if (response.success) {
      setIsLoading(false);
      return { 
        success: true, 
        message: response.data?.message || 'Account created successfully! Please wait for approval.' 
      };
    } else {
      // Handle error - extract meaningful message
      let errorMessage: string = ERROR_MESSAGES.GENERIC;
      
      if (response.message) {
        errorMessage = response.message;
      } else if (response.statusCode === 409) {
        errorMessage = ERROR_MESSAGES.EMAIL_EXISTS;
      } else if (response.statusCode === 400) {
        errorMessage = ERROR_MESSAGES.INVALID_DATA;
      } else if (response.statusCode === 0 || !response.statusCode) {
        errorMessage = ERROR_MESSAGES.NETWORK_ERROR;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, message: errorMessage };
    }
  }, []);

  // Logout handler
  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    setTokens(null);
    setError(null);
    navigate(ROUTES.AUTH.LOGIN, { replace: true });
  }, [navigate]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        // API returns user directly in data
        setUser(response.data);
        authService.updateStoredUser(response.data);
      }
    } catch {
      // Silent fail - user will be logged out on next 401
    }
  }, []);

  // Plan / add-on changes made elsewhere (e.g. an admin assigning a plan or
  // enabling Lead Generation) arrive as `account:updated`. Listening here rather
  // than in a layout means every customer page gets them, including pages outside
  // the dashboard such as Select Plan and Pending Approval.
  const isCustomer = user?.role === 'CUSTOMER';
  useEffect(() => {
    if (!isCustomer) return;
    websocketService.connect();
    return websocketService.onAccountUpdated(() => {
      void refreshUser();
    });
  }, [isCustomer, refreshUser]);

  const value = {
    user,
    tokens,
    isAuthenticated: !!user && !!tokens,
    isLoading: isLoading || !isInitialized,
    error,
    login,
    register,
    logout,
    clearError,
    refreshUser,
    updateTokens,
    updateUser,
  };

  // Don't render children until auth is initialized
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-(--color-midnight-navy)">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-(--color-signal-orange)" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

